<template>
  <div class="md-layout" v-if="enterpriseDocuments">
    <md-card class="md-layout-item md-size-50 md-small-size-100 margin-top-10">
      <md-card-content>
        <md-table class="md-table condensed">
          <md-table-toolbar>
            <div class="md-toolbar-section-start">
              <h1 class="md-title">Documents</h1>
            </div>

            <md-field class="md-toolbar-section-end">
              <md-select v-model="documentSearch.archived" @md-selected="refreshDocumentSearch">
                <md-option v-bind:value="true">Archived documents</md-option>
                <md-option v-bind:value="false">Active documents</md-option>
              </md-select>
            </md-field>
          </md-table-toolbar>

          <md-table-row>
            <md-table-head>Name</md-table-head>
            <md-table-head class="md-small-hide">Created at</md-table-head>
            <md-table-head class="text-right">Actions</md-table-head>
          </md-table-row>

          <md-table-row v-for="document in enterpriseDocuments" :key="document.id">
            <md-table-cell>
              {{ document.title }}
              <md-progress-spinner
                md-mode="indeterminate"
                md-diameter="16"
                md-stroke="3"
                v-if="downloadingDocument == document.id"
              />
            </md-table-cell>
            <md-table-cell class="md-small-hide">
              {{ $moment(document.createdAt).format('DD/MM/YYYY HH:mm:ss') }}
            </md-table-cell>
            <md-table-cell class="text-right">
              <md-menu md-direction="bottom-end" v-bind:md-offset-y="12">
                <md-button class="md-icon-button" md-menu-trigger>
                  <md-icon>more_vert</md-icon>
                </md-button>

                <md-menu-content>
                  <md-menu-item @click="downloadDocument(document)">
                    <md-icon>download</md-icon>
                    <span>Download</span>
                  </md-menu-item>
                  <md-menu-item @click="archiveDocument(document)" v-if="!document.archived">
                    <md-icon>archive</md-icon>
                    <span>Archive</span>
                  </md-menu-item>
                  <md-menu-item @click="unarchiveDocument(document)" v-if="document.archived">
                    <md-icon>unarchive</md-icon>
                    <span>Unarchive</span>
                  </md-menu-item>
                  <md-menu-item @click="requestDeleteDocument(document)" v-if="document.archived">
                    <md-icon>delete</md-icon>
                    <span>Delete</span>
                  </md-menu-item>
                </md-menu-content>
              </md-menu>
            </md-table-cell>
          </md-table-row>
        </md-table>

        <div class="md-layout md-alignment-center-center mt-1" v-if="hasNextPointer">
          <div class="md-layout-item md-size-33 md-offset-33 text-center">
            <md-button class="md-raised stretched" v-on:click="requestNextPage">
              More
            </md-button>
          </div>
        </div>

        <md-dialog-confirm
          :md-active.sync="deletingDocumentDialog"
          v-if="deletingDocument"
          md-title="Delete document?"
          :md-content="`Document name:<br />${deletingDocument.title}`"
          @md-cancel="cancelDeleteDocument"
          @md-confirm="deleteDocument(deletingDocument)"
        />

        <form v-if="uploading">
          <md-progress-bar v-if="uploadInProgress" md-mode="indeterminate"></md-progress-bar>

          <div v-if="!uploadInProgress">
            <md-field>
              <label for="file">File</label>
              <md-file
                name="file"
                ref="fileInput"
                v-model="newDocument.file"
                @md-change="uploadFile" />
            </md-field>

            <md-button class="md-raised" @click="cancelUpload">
              Cancel
            </md-button>
          </div>
        </form>

        <div>
          <md-empty-state
            md-icon="table"
            md-label="No documents in this view"
            md-description="We haven't found documents for these criteria."
            v-if="enterpriseDocuments.length === 0 && !uploading"
          />
        </div>
      </md-card-content>
      <md-card-actions>
        <md-button @click="requestUpload" v-if="!uploading">
          Upload
        </md-button>
      </md-card-actions>
    </md-card>
  </div>
</template>
<style lang="scss">
.md-empty-state {
  .md-icon {
    display: flex;
    justify-content: flex-start;
  }
}
</style>
<script type="ts">
import EnterpriseService from '@/services/enterprise/EnterpriseService';
import EnterpriseDocument from '@/datatypes/enterprise-document';
import Enterprise from '@/datatypes/enterprise';
import camelcaseKeysDeep from 'camelcase-keys-deep';
import jsFileDownload from 'js-file-download';

export default {
  name: 'enterprise-documents',
  props: {
    enterprise: {
      type: Enterprise,
      required: true,
    },
  },
  watch: {
    localEnterprise(enterprise) {
      return this.loadEnterpriseDocuments(enterprise);
    },
  },
  data: () => ({
    enterpriseService: new EnterpriseService(),
    enterpriseDocuments: undefined,
    uploading: false,
    newDocument: {
      title: '',
      file: undefined,
    },
    deletingDocument: undefined,
    downloadingDocument: undefined,
    documentSearch: {
      archived: false,
      next: undefined,
    },
    nextPointer: undefined,
    uploadInProgress: false,
  }),
  methods: {
    refreshDocumentSearch() {
      this.loadEnterpriseDocuments(this.enterprise);
    },
    loadEnterpriseDocuments(enterprise) {
      const service = this.enterpriseService.getEnterpriseDocuments(
        enterprise.id, this.documentSearch,
      );

      service.then((response) => {
        if (!this.documentSearch.next) this.enterpriseDocuments = [];

        this.nextPointer = response.headers.next;
        if (response.data.length === 0) this.nextPointer = undefined;

        this.enterpriseDocuments = this.enterpriseDocuments.concat(response.data.map(
          (data) => new EnterpriseDocument(camelcaseKeysDeep(data)),
        ));
      });

      return service;
    },
    requestNextPage() {
      this.documentSearch.next = this.nextPointer;
      this.loadEnterpriseDocuments(this.enterprise);
    },
    requestUpload() {
      this.uploading = true;
      this.$nextTick(() => {
        this.$refs.fileInput.$refs.inputFile.click();
      });
    },
    cancelUpload() {
      this.uploading = false;
      this.resetNewDocument();
    },
    resetNewDocument() {
      this.newDocument = {
        title: '',
        file: undefined,
      };
    },
    uploadFile(files) {
      const reader = new FileReader();

      reader.onload = (e) => {
        this.uploadInProgress = true;
        this.enterpriseService.uploadDocument(
          this.enterprise.id, {
            file: e.target.result,
            title: files[0].name,
          },
        ).then(() => {
          this.loadEnterpriseDocuments(this.enterprise).then(() => {
            this.resetNewDocument();
          });
        }).catch((error) => {
          this.$emit('statusMessage', error.response.data.message);
          this.resetNewDocument();
        }).finally(() => {
          this.uploading = false;
          this.uploadInProgress = false;
        });
      };

      reader.readAsDataURL(files[0]);
    },
    downloadDocument(document) {
      this.downloadingDocument = document.id;
      this.enterpriseService.downloadDocument(this.enterprise.id, document.id).then((response) => {
        if (response.data.data) {
          const bytes = atob(response.data.data);
          const byteNumbers = new Array(bytes.length);
          for (let i = 0; i < bytes.length; i += 1) {
            byteNumbers[i] = bytes.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: response.data.content_type });

          jsFileDownload(blob, response.data.filename);
        }
      }).finally(() => {
        this.downloadingDocument = undefined;
      });
    },
    archiveDocument(document) {
      this.enterpriseService.archiveDocument(this.enterprise.id, document.id).then(() => {
        this.loadEnterpriseDocuments(this.enterprise);
      });
    },
    unarchiveDocument(document) {
      this.enterpriseService.unarchiveDocument(this.enterprise.id, document.id).then(() => {
        this.loadEnterpriseDocuments(this.enterprise);
      });
    },
    requestDeleteDocument(document) {
      this.deletingDocument = document;
    },
    deleteDocument(document) {
      this.enterpriseService.deleteDocument(this.enterprise.id, document.id).then(() => {
        this.loadEnterpriseDocuments(this.enterprise);
      });
    },
    cancelDeleteDocument() {
      this.deletingDocument = undefined;
    },
  },
  computed: {
    localEnterprise: {
      get() {
        return this.enterprise;
      },
      set(value) {
        this.$emit('enterpriseChange', value);
      },
    },
    hasNextPointer() {
      return this.nextPointer !== undefined;
    },
    deletingDocumentDialog: {
      get() {
        return this.deletingDocument !== undefined;
      },
      set(value) {
        if (value === false) {
          this.deletingDocument = undefined;
        }
      },
    },
  },
};
</script>
